// Room Management Service for Admin Dashboard
// Connects to Firebase real-time database for room management

import { 
  collection, 
  doc, 
  getDocs, 
  getDoc,
  addDoc, 
  updateDoc, 
  deleteDoc,
  query, 
  orderBy, 
  onSnapshot, 
  where,
  Timestamp
} from 'firebase/firestore';
import { db } from '../config/firebase';

// Enhanced interfaces for room management
export interface RoomPatient {
  id: string;
  name: string;
  patientId: string;
  bedNumber: string;
  admissionDate: string;
  condition?: string;
  contactNumber?: string;
  emergencyContact?: string;
}

export interface Room {
  id: string;
  number: string;
  roomNumber: string;
  type: 'General Ward' | 'Private Room' | 'ICU' | 'VIP';
  roomType?: string;
  floor: number;
  status: 'Available' | 'Occupied' | 'Partially Occupied' | 'Under Maintenance' | 'Out of Order';
  statusColor?: string;
  capacity?: number;
  occupancy?: {
    current: number;
    total: number;
  };
  patients?: RoomPatient[];
  amenities: string[];
  rate: number;
  lastCleaned?: string;
  nextMaintenance?: string;
  department?: string;
  createdAt?: string;
  updatedAt?: string;
  // Bed management
  occupiedBeds?: Array<{
    bedNumber: string;
    patientName: string;
    patientId: string;
    admissionDate: string;
  }>;
  availableBeds?: string[] | Array<{
    bedNumber: string;
    isOccupied: boolean;
    patientId?: string;
    patientName?: string;
    occupiedAt?: string;
  }>;
  bedLabels?: string[];
  totalBeds?: number;
  currentOccupancy?: number;
  // Additional room info
  patientName?: string;
  patientId?: string;
  admissionDate?: string;
}

export interface RoomStats {
  totalRooms: number;
  occupiedRooms: number;
  availableRooms: number;
  maintenanceRooms: number;
  occupancyRate: number;
  totalBeds: number;
  occupiedBeds: number;
  availableBeds: number;
  bedOccupancyRate: number;
}

export interface CreateRoomData {
  roomNumber: string;
  type: Room['type'];
  floor: number | string;
  amenities: string[];
  rate: number | string;
  capacity?: number | string;
  department?: string;
  totalBeds?: number;
}

export class RoomManagementService {
  private static readonly COLLECTION_NAME = 'rooms';

  // Convert Firebase timestamp to readable date
  private static convertFirebaseTimestamp(timestamp: any): string {
    if (!timestamp) return '';
    
    try {
      if (timestamp && typeof timestamp === 'object' && 'toDate' in timestamp) {
        return timestamp.toDate().toLocaleDateString('en-GB');
      }
      
      if (timestamp instanceof Date) {
        return timestamp.toLocaleDateString('en-GB');
      }
      
      if (typeof timestamp === 'string') {
        const date = new Date(timestamp);
        return isNaN(date.getTime()) ? timestamp : date.toLocaleDateString('en-GB');
      }
      
      if (typeof timestamp === 'number') {
        return new Date(timestamp).toLocaleDateString('en-GB');
      }
    } catch (error) {
      console.warn('Error converting timestamp:', error);
    }
    
    return '';
  }

  // Transform Firebase room data to our Room interface
  private static transformFirebaseRoom(firebaseRoom: any): Room {
    try {
      // Validate input
      if (!firebaseRoom || typeof firebaseRoom !== 'object') {
        console.warn('Invalid Firebase room data:', firebaseRoom);
        throw new Error('Invalid room data received from Firebase');
      }

      // Calculate total beds - check multiple possible fields
      let totalBeds = Number(firebaseRoom.totalBeds || firebaseRoom.capacity || firebaseRoom.beds || 0);
      
      // If still 0, apply smart defaults based on room type
      if (totalBeds === 0) {
        const roomType = String(firebaseRoom.roomType || firebaseRoom.type || '').toLowerCase();
        if (roomType.includes('icu')) {
          totalBeds = 6;
        } else if (roomType.includes('vip') || roomType.includes('private')) {
          totalBeds = 1;
        } else {
          totalBeds = 10; // General ward default
        }
        console.warn(`⚠️ Room ${firebaseRoom.roomNumber || firebaseRoom.number} has no bed data. Applied default: ${totalBeds} beds for ${roomType || 'general'} ward`);
      }
      
      const occupiedBedsArray = Array.isArray(firebaseRoom.occupiedBeds) ? firebaseRoom.occupiedBeds : [];
      const occupiedCount = occupiedBedsArray.length;
      
      // Generate bedLabels if missing
      const roomNumber = String(firebaseRoom.roomNumber || firebaseRoom.number || firebaseRoom.id || '');
      let bedLabels = Array.isArray(firebaseRoom.bedLabels) ? firebaseRoom.bedLabels.filter((b: any) => typeof b === 'string') : [];
      if (bedLabels.length === 0 && totalBeds > 0) {
        // Auto-generate bed labels: 101A, 101B, etc.
        bedLabels = Array.from({ length: totalBeds }, (_, i) => `${roomNumber}${String.fromCharCode(65 + i)}`);
        console.log(`🔧 Auto-generated ${bedLabels.length} bed labels for room ${roomNumber}:`, bedLabels);
      }
      
      // Handle availableBeds structure
      let availableBeds: any[] = [];
      if (Array.isArray(firebaseRoom.availableBeds) && firebaseRoom.availableBeds.length > 0) {
        availableBeds = firebaseRoom.availableBeds;
      } else if (bedLabels.length > 0) {
        // Create bed objects from bedLabels if availableBeds is missing
        availableBeds = bedLabels.map((bedNum: string) => ({
          bedNumber: bedNum,
          isOccupied: false
        }));
        console.log(`🔧 Created ${availableBeds.length} bed objects for room ${roomNumber}`);
      }
      
      const room: Room = {
        id: String(firebaseRoom.id || ''),
        number: String(firebaseRoom.roomNumber || firebaseRoom.number || firebaseRoom.id || ''),
        roomNumber: String(firebaseRoom.roomNumber || firebaseRoom.number || firebaseRoom.id || ''),
        type: this.normalizeRoomType(firebaseRoom.roomType || firebaseRoom.type),
        roomType: String(firebaseRoom.roomType || firebaseRoom.type || 'General Ward'),
        floor: Number(firebaseRoom.floor) || 1,
        status: this.normalizeRoomStatus(firebaseRoom.status),
        statusColor: this.getStatusColor(firebaseRoom.status),
        capacity: totalBeds, // Now guaranteed to be at least 1
        rate: Number(firebaseRoom.rate || firebaseRoom.dailyRate || firebaseRoom.price) || 1000,
        amenities: Array.isArray(firebaseRoom.amenities) ? firebaseRoom.amenities.filter((a: any) => typeof a === 'string') : ['Bed', 'Bathroom'],
        department: String(firebaseRoom.department || 'General'),
        lastCleaned: this.convertFirebaseTimestamp(firebaseRoom.lastCleaned),
        nextMaintenance: this.convertFirebaseTimestamp(firebaseRoom.nextMaintenance),
        createdAt: this.convertFirebaseTimestamp(firebaseRoom.createdAt),
        updatedAt: this.convertFirebaseTimestamp(firebaseRoom.updatedAt),
        
        // Occupancy info
        occupancy: {
          current: occupiedCount,
          total: totalBeds // Now guaranteed to have proper value
        },
        
        // Bed management - matching mobile app structure
        totalBeds: totalBeds, // Now guaranteed to have proper value
        bedLabels: bedLabels,
        occupiedBeds: Array.isArray(firebaseRoom.occupiedBeds) ? firebaseRoom.occupiedBeds : [],
        // Keep availableBeds as-is (can be strings or objects with {bedNumber, isOccupied})
        availableBeds: availableBeds,
        
        // Patient info
        patientName: firebaseRoom.patientName ? String(firebaseRoom.patientName) : undefined,
        patientId: firebaseRoom.patientId ? String(firebaseRoom.patientId) : undefined,
        admissionDate: this.convertFirebaseTimestamp(firebaseRoom.admissionDate)
      };

    // Calculate occupancy from availableBeds array (new structure)
    const totalCapacity = totalBeds; // Already guaranteed to have proper value
    let currentOccupancy = 0;
    
    // Check if using new availableBeds structure with isOccupied property
    if (availableBeds && Array.isArray(availableBeds) && availableBeds.length > 0) {
      currentOccupancy = availableBeds.filter((bed: any) => {
        if (typeof bed === 'object' && bed !== null) {
          return bed.isOccupied === true;
        }
        return false;
      }).length;
    } else if (firebaseRoom.occupiedBeds && Array.isArray(firebaseRoom.occupiedBeds)) {
      // Fallback to old occupiedBeds structure
      currentOccupancy = firebaseRoom.occupiedBeds.length;
    } else if (firebaseRoom.patientName) {
      currentOccupancy = 1;
    } else if (firebaseRoom.currentOccupancy !== undefined) {
      currentOccupancy = Number(firebaseRoom.currentOccupancy) || 0;
    }
    
    // Update room occupancy with calculated values
    room.occupancy = {
      current: currentOccupancy,
      total: totalCapacity
    };
    
    console.log(`📊 Room ${roomNumber}: ${currentOccupancy}/${totalCapacity} beds occupied (${availableBeds.length} bed objects)`);

    // Build patients array from occupancy data
    if (room.occupiedBeds && Array.isArray(room.occupiedBeds) && room.occupiedBeds.length > 0) {
      room.patients = room.occupiedBeds
        .filter(bedNumber => bedNumber && typeof bedNumber === 'string') // Filter out invalid bed numbers
        .map((bedNumber, index) => ({
          id: `patient_${room.id}_${index}`,
          name: `Patient ${index + 1}`, // We'll need to get actual patient names
          patientId: `KBR-${room.type === 'ICU' ? 'ICU' : room.type === 'VIP' ? 'VIP' : 'IP'}-${new Date().getFullYear()}-${Math.random().toString().substr(2, 10)}`,
          bedNumber: String(bedNumber),
          admissionDate: this.convertFirebaseTimestamp(room.admissionDate) || new Date().toLocaleDateString('en-GB'),
          condition: room.type === 'ICU' ? 'Critical' : 'Stable'
        }));
    } else if (room.patientName && typeof room.patientName === 'string') {
      room.patients = [{
        id: `patient_${room.id}`,
        name: String(room.patientName),
        patientId: String(room.patientId || `KBR-IP-${new Date().getFullYear()}-${Math.random().toString().substr(2, 10)}`),
        bedNumber: `${room.number}A`,
        admissionDate: this.convertFirebaseTimestamp(room.admissionDate) || new Date().toLocaleDateString('en-GB'),
        condition: room.type === 'ICU' ? 'Critical' : 'Stable'
      }];
    } else {
      room.patients = [];
    }

    return room;
    } catch (error) {
      console.error('Error transforming Firebase room data:', error, firebaseRoom);
      
      // Return a fallback room object to prevent crashes
      return {
        id: String(firebaseRoom?.id || Math.random().toString()),
        number: String(firebaseRoom?.roomNumber || firebaseRoom?.number || 'Unknown'),
        roomNumber: String(firebaseRoom?.roomNumber || firebaseRoom?.number || 'Unknown'),
        type: 'General Ward' as Room['type'],
        roomType: 'General Ward',
        floor: 1,
        status: 'Available' as Room['status'],
        statusColor: '#10B981',
        capacity: 1,
        rate: 1000,
        amenities: ['Bed', 'Bathroom'],
        department: 'General',
        occupancy: { current: 0, total: 1 },
        patients: [],
        occupiedBeds: [],
        availableBeds: [],
        bedLabels: [],
        totalBeds: 1
      };
    }
  }

  // Normalize room type to match our interface
  private static normalizeRoomType(type: any): Room['type'] {
    if (!type) return 'General Ward';
    
    const typeStr = type.toString().toLowerCase();
    if (typeStr.includes('general') || typeStr.includes('ward')) return 'General Ward';
    if (typeStr.includes('private')) return 'Private Room';
    if (typeStr.includes('icu') || typeStr.includes('intensive')) return 'ICU';
    if (typeStr.includes('vip') || typeStr.includes('suite')) return 'VIP';
    
    return 'General Ward';
  }

  // Normalize room status
  private static normalizeRoomStatus(status: any): Room['status'] {
    if (!status) return 'Available';
    
    const statusStr = status.toString().toLowerCase();
    
    // Check for "partially occupied" first before checking for "occupied"
    if (statusStr.includes('partially') || statusStr.includes('partial')) return 'Partially Occupied';
    if (statusStr.includes('occupied') || statusStr.includes('busy')) return 'Occupied';
    if (statusStr.includes('maintenance') || statusStr.includes('repair')) return 'Under Maintenance';
    if (statusStr.includes('out of order') || statusStr.includes('unavailable')) return 'Out of Order';
    if (statusStr.includes('available') || statusStr.includes('free')) return 'Available';
    
    return 'Available';
  }

  // Get status color
  private static getStatusColor(status: any): string {
    const normalizedStatus = this.normalizeRoomStatus(status);
    switch (normalizedStatus) {
      case 'Available': return '#10B981';
      case 'Occupied': return '#EF4444';
      case 'Partially Occupied': return '#F59E0B';
      case 'Under Maintenance': return '#F59E0B';
      case 'Out of Order': return '#EF4444';
      default: return '#10B981';
    }
  }

  // Get all rooms from Firebase
  static async getRooms(): Promise<Room[]> {
    try {
      console.log('🏥 Fetching rooms from Firebase...');
      
      const roomsRef = collection(db, this.COLLECTION_NAME);
      const q = query(roomsRef, orderBy('roomNumber', 'asc'));
      const querySnapshot = await getDocs(q);
      
      const rooms: Room[] = [];
      querySnapshot.forEach((doc) => {
        const firebaseRoom = { id: doc.id, ...doc.data() };
        const transformedRoom = this.transformFirebaseRoom(firebaseRoom);
        rooms.push(transformedRoom);
      });
      
      console.log(`✅ Successfully fetched ${rooms.length} rooms from Firebase`);
      return rooms;
    } catch (error) {
      console.error('❌ Error fetching rooms:', error);
      // Return empty array instead of throwing to prevent app crashes
      return [];
    }
  }

  // Subscribe to real-time room updates
  static subscribeToRooms(callback: (rooms: Room[]) => void): () => void {
    try {
      console.log('🔄 Setting up real-time rooms listener...');
      
      const roomsRef = collection(db, this.COLLECTION_NAME);
      const q = query(roomsRef, orderBy('roomNumber', 'asc'));
      
      const unsubscribe = onSnapshot(q, 
        (querySnapshot) => {
          const rooms: Room[] = [];
          querySnapshot.forEach((doc) => {
            const firebaseRoom = { id: doc.id, ...doc.data() };
            const transformedRoom = this.transformFirebaseRoom(firebaseRoom);
            rooms.push(transformedRoom);
          });
          
          console.log(`🔄 Real-time rooms update: ${rooms.length} rooms`);
          callback(rooms);
        },
        (error) => {
          console.error('❌ Real-time rooms listener error:', error);
          // Return empty array on error to prevent crashes
          callback([]);
        }
      );
      
      return unsubscribe;
    } catch (error) {
      console.error('❌ Error setting up rooms listener:', error);
      // Return a no-op function
      return () => {};
    }
  }

  // Create a new room
  static async createRoom(roomData: CreateRoomData): Promise<{ success: boolean; data?: Room; error?: string }> {
    try {
      console.log('➕ Creating new room in Firebase...', roomData);
      
      // Check if room number already exists
      const roomsRef = collection(db, this.COLLECTION_NAME);
      const q = query(roomsRef, where('roomNumber', '==', roomData.roomNumber));
      const existingRooms = await getDocs(q);
      
      if (!existingRooms.empty) {
        return {
          success: false,
          error: `Room ${roomData.roomNumber} already exists`
        };
      }

      // Generate bed labels for this room
      const generateBedLabels = (totalBeds: number) => {
        const bedLabels = [];
        for (let i = 1; i <= totalBeds; i++) {
          bedLabels.push(`${roomData.roomNumber}${String.fromCharCode(64 + i)}`); // A, B, C, etc.
        }
        return bedLabels;
      };

      const totalBeds = roomData.capacity || roomData.totalBeds || 1;
      const totalBedsNum = typeof totalBeds === 'string' ? parseInt(totalBeds) : totalBeds;
      const bedLabels = generateBedLabels(totalBedsNum);
      
      // Create bed objects with proper structure
      const availableBeds = bedLabels.map(bedLabel => ({
        bedNumber: bedLabel,
        isOccupied: false
      }));
      
      const room = {
        roomNumber: roomData.roomNumber,
        type: roomData.type,
        roomType: roomData.type,
        floor: typeof roomData.floor === 'string' ? parseInt(roomData.floor) : roomData.floor,
        amenities: roomData.amenities,
        rate: typeof roomData.rate === 'string' ? parseInt(roomData.rate) : roomData.rate,
        capacity: totalBedsNum,
        totalBeds: totalBedsNum,
        department: roomData.department || 'General',
        status: 'Available',
        statusColor: '#10B981',
        createdAt: new Date().toISOString(),
        updatedAt: new Date().toISOString(),
        currentOccupancy: 0,
        isOccupied: false,
        
        // Bed management with proper structure
        occupiedBeds: [],
        availableBeds: availableBeds,
        bedLabels: bedLabels,
        
        // Occupancy tracking
        occupancy: {
          current: 0,
          total: totalBedsNum
        },
        
        // Patient info (initially empty)
        patientName: null,
        patientId: null,
        admissionDate: null
      };

      const docRef = await addDoc(collection(db, this.COLLECTION_NAME), room);
      
      console.log(`✅ Room ${roomData.roomNumber} created successfully with ID: ${docRef.id}`);
      
      const createdRoom = this.transformFirebaseRoom({ id: docRef.id, ...room });
      
      return {
        success: true,
        data: createdRoom
      };
    } catch (error: any) {
      console.error('❌ Error creating room:', error);
      return {
        success: false,
        error: `Failed to create room: ${error?.message || error}`
      };
    }
  }

  // Update room information
  static async updateRoom(roomId: string, updateData: Partial<Room>): Promise<{ success: boolean; error?: string }> {
    try {
      console.log('📝 Updating room in Firebase...', roomId, updateData);
      
      const roomRef = doc(db, this.COLLECTION_NAME, roomId);
      
      // Prepare the update data
      const firebaseUpdateData: any = {
        ...updateData,
        updatedAt: new Date().toISOString()
      };

      // Remove undefined fields
      Object.keys(firebaseUpdateData).forEach((key: string) => {
        if (firebaseUpdateData[key] === undefined) {
          delete firebaseUpdateData[key];
        }
      });

      await updateDoc(roomRef, firebaseUpdateData);
      
      console.log(`✅ Room ${roomId} updated successfully`);
      return { success: true };
    } catch (error: any) {
      console.error('❌ Error updating room:', error);
      return {
        success: false,
        error: `Failed to update room: ${error?.message || error}`
      };
    }
  }

  // Delete room
  static async deleteRoom(roomId: string): Promise<{ success: boolean; error?: string }> {
    try {
      console.log('🗑️ Deleting room from Firebase...', roomId);
      
      const roomRef = doc(db, this.COLLECTION_NAME, roomId);
      await deleteDoc(roomRef);
      
      console.log(`✅ Room ${roomId} deleted successfully`);
      return { success: true };
    } catch (error: any) {
      console.error('❌ Error deleting room:', error);
      return {
        success: false,
        error: `Failed to delete room: ${error?.message || error}`
      };
    }
  }

  // Assign patient to room
  static async assignPatientToRoom(roomId: string, patientData: RoomPatient): Promise<{ success: boolean; error?: string }> {
    try {
      console.log('👤 Assigning patient to room...', roomId, patientData);
      
      const roomRef = doc(db, this.COLLECTION_NAME, roomId);
      const roomDoc = await getDoc(roomRef);
      
      if (!roomDoc.exists()) {
        return {
          success: false,
          error: 'Room not found'
        };
      }
      
      const roomData = roomDoc.data();
      const currentPatients = roomData.patients || [];
      
      // Add new patient to patients array
      const updatedPatients = [...currentPatients, {
        id: patientData.id,
        name: patientData.name,
        patientId: patientData.patientId,
        bedNumber: patientData.bedNumber,
        admissionDate: patientData.admissionDate,
        condition: patientData.condition,
        contactNumber: patientData.contactNumber,
        emergencyContact: patientData.emergencyContact
      }];
      
      // Calculate new status
      const totalBeds = roomData.capacity || roomData.totalBeds || 1;
      let newStatus: Room['status'] = 'Available';
      let newStatusColor = '#10B981';
      
      if (updatedPatients.length >= totalBeds) {
        newStatus = 'Occupied';
        newStatusColor = '#EF4444';
      } else if (updatedPatients.length > 0) {
        newStatus = 'Partially Occupied';
        newStatusColor = '#F59E0B';
      }
      
      await updateDoc(roomRef, {
        patients: updatedPatients,
        status: newStatus,
        statusColor: newStatusColor,
        // Also update legacy fields for backward compatibility
        patientName: patientData.name,
        patientId: patientData.patientId,
        admissionDate: patientData.admissionDate,
        updatedAt: new Date().toISOString(),
        occupancy: {
          current: updatedPatients.length,
          total: totalBeds
        }
      });
      
      console.log(`✅ Patient ${patientData.name} assigned to room ${roomId}`);
      return { success: true };
    } catch (error: any) {
      console.error('❌ Error assigning patient to room:', error);
      return {
        success: false,
        error: `Failed to assign patient: ${error?.message || error}`
      };
    }
  }

  // Discharge patient from room
  static async dischargePatient(roomId: string, patientId?: string): Promise<{ success: boolean; error?: string }> {
    try {
      console.log('🚪 Discharging patient from room...', roomId, patientId);
      
      const roomRef = doc(db, this.COLLECTION_NAME, roomId);
      const roomDoc = await getDoc(roomRef);
      
      if (!roomDoc.exists()) {
        return {
          success: false,
          error: 'Room not found'
        };
      }
      
      const roomData = roomDoc.data();
      const currentPatients = roomData.patients || [];
      
      let updatedPatients: RoomPatient[] = [];
      
      if (patientId) {
        // Remove specific patient
        updatedPatients = currentPatients.filter((p: RoomPatient) => p.id !== patientId && p.patientId !== patientId);
      } else {
        // Remove all patients (full discharge)
        updatedPatients = [];
      }
      
      // Calculate new status - room becomes Available when no patients
      const totalBeds = roomData.capacity || roomData.totalBeds || 1;
      const bedLabels = roomData.bedLabels || [];
      const currentOccupiedBeds = roomData.occupiedBeds || [];
      
      // Update occupiedBeds array - remove discharged patient's bed
      let updatedOccupiedBeds = currentOccupiedBeds;
      if (patientId) {
        // Remove specific patient's bed
        updatedOccupiedBeds = currentOccupiedBeds.filter((bed: any) => 
          bed.patientId !== patientId
        );
      } else {
        // Clear all occupied beds
        updatedOccupiedBeds = [];
      }
      
      // Recalculate available beds - all beds that are not occupied
      const occupiedBedNumbers = updatedOccupiedBeds.map((bed: any) => bed.bedNumber);
      const updatedAvailableBeds = bedLabels.filter((bed: string) => !occupiedBedNumbers.includes(bed));
      
      let newStatus: Room['status'] = 'Available';
      let newStatusColor = '#10B981';
      
      if (updatedPatients.length === 0 && updatedOccupiedBeds.length === 0) {
        // No patients - room is Available
        newStatus = 'Available';
        newStatusColor = '#10B981';
      } else if (updatedOccupiedBeds.length >= totalBeds) {
        newStatus = 'Occupied';
        newStatusColor = '#EF4444';
      } else if (updatedOccupiedBeds.length > 0) {
        newStatus = 'Partially Occupied';
        newStatusColor = '#F59E0B';
      }
      
      await updateDoc(roomRef, {
        patients: updatedPatients,
        occupiedBeds: updatedOccupiedBeds,
        availableBeds: updatedAvailableBeds,
        status: newStatus,
        statusColor: newStatusColor,
        // Clear legacy fields if no patients remaining
        patientName: updatedPatients.length === 0 ? null : (updatedPatients[0]?.name || null),
        patientId: updatedPatients.length === 0 ? null : (updatedPatients[0]?.patientId || null),
        admissionDate: updatedPatients.length === 0 ? null : (updatedPatients[0]?.admissionDate || null),
        updatedAt: new Date().toISOString(),
        occupancy: {
          current: updatedOccupiedBeds.length,
          total: totalBeds
        }
      });
      
      console.log(`✅ Patient discharged from room ${roomId}`);
      return { success: true };
    } catch (error: any) {
      console.error('❌ Error discharging patient:', error);
      return {
        success: false,
        error: `Failed to discharge patient: ${error?.message || error}`
      };
    }
  }

  // Update room status
  static async updateRoomStatus(roomId: string, status: Room['status']): Promise<{ success: boolean; error?: string }> {
    try {
      console.log('🔄 Updating room status...', roomId, status);
      
      const roomRef = doc(db, this.COLLECTION_NAME, roomId);
      const roomDoc = await getDoc(roomRef);
      
      if (!roomDoc.exists()) {
        return { success: false, error: 'Room not found' };
      }
      
      const roomData = roomDoc.data();
      const totalBeds = roomData.totalBeds || roomData.capacity || 1;
      
      // Calculate occupancy based on status if manually set
      let currentOccupancy = roomData.currentOccupancy || 0;
      let availableBeds = roomData.availableBeds || [];
      
      // If status is manually changed, update occupancy to match
      if (status === 'Available' && currentOccupancy > 0) {
        // Clear all beds
        currentOccupancy = 0;
        if (Array.isArray(availableBeds) && availableBeds.length > 0) {
          availableBeds = availableBeds.map((bed: any) => ({
            bedNumber: typeof bed === 'string' ? bed : bed.bedNumber,
            isOccupied: false
          }));
        }
      } else if (status === 'Occupied' && currentOccupancy < totalBeds) {
        // Mark all beds as occupied
        currentOccupancy = totalBeds;
        if (Array.isArray(availableBeds) && availableBeds.length > 0) {
          availableBeds = availableBeds.map((bed: any) => ({
            bedNumber: typeof bed === 'string' ? bed : bed.bedNumber,
            isOccupied: true,
            patientName: 'Occupied',
            patientId: 'manual',
            occupiedAt: new Date().toISOString()
          }));
        }
      }
      
      await updateDoc(roomRef, {
        status: status,
        statusColor: this.getStatusColor(status),
        currentOccupancy: currentOccupancy,
        availableBeds: availableBeds,
        occupancy: {
          current: currentOccupancy,
          total: totalBeds
        },
        updatedAt: new Date().toISOString()
      });
      
      console.log(`✅ Room ${roomId} status updated to ${status} with occupancy ${currentOccupancy}/${totalBeds}`);
      return { success: true };
    } catch (error: any) {
      console.error('❌ Error updating room status:', error);
      return {
        success: false,
        error: `Failed to update room status: ${error?.message || error}`
      };
    }
  }

  // Release patient from specific bed (matching mobile app functionality)
  static async releasePatientFromBed(roomId: string, bedNumber: string, patientId: string): Promise<{ success: boolean; error?: string }> {
    try {
      console.log(`🛏️ Releasing patient from bed ${bedNumber} in room ${roomId}...`);
      
      const roomRef = doc(db, this.COLLECTION_NAME, roomId);
      const roomDoc = await getDoc(roomRef);
      
      if (!roomDoc.exists()) {
        return { success: false, error: 'Room not found' };
      }
      
      const roomData = roomDoc.data();
      const currentAvailableBeds = roomData.availableBeds || [];
      const bedLabels = roomData.bedLabels || [];
      const totalBeds = roomData.totalBeds || roomData.capacity || 1;
      
      // Update availableBeds array structure
      let updatedBeds: Array<{
        bedNumber: string;
        isOccupied: boolean;
        patientId?: string;
        patientName?: string;
        occupiedAt?: string;
      }> = [];
      
      if (Array.isArray(currentAvailableBeds) && currentAvailableBeds.length > 0) {
        // Update existing bed structure
        updatedBeds = currentAvailableBeds.map((bed: any) => {
          const bedNum = typeof bed === 'string' ? bed : bed.bedNumber;
          
          if (bedNum === bedNumber) {
            // Release this bed - only include defined fields
            return {
              bedNumber: bedNum,
              isOccupied: false
            };
          }
          
          // Keep existing bed data
          return typeof bed === 'object' ? bed : { bedNumber: bed, isOccupied: false };
        });
      } else {
        // Create bed structure if it doesn't exist
        updatedBeds = bedLabels.map((bedNum: string) => ({
          bedNumber: bedNum,
          isOccupied: bedNum !== bedNumber,
          patientId: undefined,
          patientName: undefined,
          occupiedAt: undefined
        }));
      }
      
      // Count occupied beds
      const occupiedCount = updatedBeds.filter(bed => bed.isOccupied).length;
      
      // Calculate new room status
      let newStatus: Room['status'] = 'Available';
      let newStatusColor = '#10B981';
      
      if (occupiedCount === 0) {
        newStatus = 'Available';
        newStatusColor = '#10B981';
      } else if (occupiedCount >= totalBeds) {
        newStatus = 'Occupied';
        newStatusColor = '#EF4444';
      } else if (occupiedCount > 0) {
        newStatus = 'Partially Occupied';
        newStatusColor = '#F59E0B';
      }
      
      await updateDoc(roomRef, {
        availableBeds: updatedBeds,
        currentOccupancy: occupiedCount,
        status: newStatus,
        statusColor: newStatusColor,
        updatedAt: new Date().toISOString(),
        occupancy: {
          current: occupiedCount,
          total: totalBeds
        }
      });
      
      console.log(`✅ Patient released from bed ${bedNumber}. Room status: ${newStatus}`);
      return { success: true };
      
    } catch (error: any) {
      console.error('❌ Error releasing patient from bed:', error);
      return { 
        success: false, 
        error: `Failed to release patient from bed: ${error?.message || error}` 
      };
    }
  }

  // Update room availability when patient is discharged or deleted
  static async updateRoomAvailabilityForPatient(
    patientId: string, 
    roomNumber: string, 
    bedNumber: string
  ): Promise<{ success: boolean; message: string }> {
    try {
      console.log(`🛏️ Updating room availability for patient ${patientId} in room ${roomNumber}, bed ${bedNumber}...`);
      
      if (!roomNumber || !bedNumber) {
        console.log('⚠️ No room or bed assigned to this patient');
        return { success: true, message: 'No room to update' };
      }
      
      // Find the room document
      const roomsRef = collection(db, this.COLLECTION_NAME);
      const roomsSnapshot = await getDocs(roomsRef);
      const roomDoc = roomsSnapshot.docs.find(doc => {
        const data = doc.data();
        return data.roomNumber === roomNumber || data.number === roomNumber || doc.id === roomNumber;
      });
      
      if (!roomDoc) {
        console.log(`⚠️ Room ${roomNumber} not found in database`);
        return { success: false, message: 'Room not found' };
      }
      
      const roomData = roomDoc.data();
      const totalBeds = roomData.totalBeds || roomData.capacity || 1;
      
      // Get current bed structure
      let updatedBeds: Array<{
        bedNumber: string;
        isOccupied: boolean;
        patientId?: string;
        patientName?: string;
        occupiedAt?: string;
      }> = [];
      
      if (roomData.availableBeds && Array.isArray(roomData.availableBeds)) {
        // Update existing bed structure - mark the specific bed as available
        updatedBeds = roomData.availableBeds.map((bed: any) => {
          const bedNum = typeof bed === 'string' ? bed : (bed.bedNumber || bed.number || bed.id);
          
          if (bedNum === bedNumber) {
            // Release this bed
            return {
              bedNumber: bedNum,
              isOccupied: false
            };
          }
          
          // Keep existing bed data
          return typeof bed === 'object' ? bed : { bedNumber: bed, isOccupied: false };
        });
      } else {
        // Create new bed structure
        const bedLabels = roomData.bedLabels || 
          Array.from({ length: totalBeds }, (_, i) => `${roomNumber}${String.fromCharCode(65 + i)}`);
        
        updatedBeds = bedLabels.map((bedNum: string) => ({
          bedNumber: bedNum,
          isOccupied: bedNum !== bedNumber
        }));
      }
      
      // Calculate current occupancy
      const currentOccupancy = updatedBeds.filter((bed: any) => bed.isOccupied).length;
      
      // Determine room status
      let status: Room['status'] = 'Available';
      let statusColor = '#10B981';
      
      if (currentOccupancy === 0) {
        status = 'Available';
        statusColor = '#10B981';
      } else if (currentOccupancy >= totalBeds) {
        status = 'Occupied';
        statusColor = '#EF4444';
      } else {
        status = 'Partially Occupied';
        statusColor = '#F59E0B';
      }
      
      // Update room in Firebase
      await updateDoc(doc(db, this.COLLECTION_NAME, roomDoc.id), {
        availableBeds: updatedBeds,
        currentOccupancy: currentOccupancy,
        status: status,
        statusColor: statusColor,
        isOccupied: currentOccupancy > 0,
        occupancy: {
          current: currentOccupancy,
          total: totalBeds
        },
        updatedAt: new Date().toISOString()
      });
      
      console.log(`✅ Room ${roomNumber} updated: ${status} (${currentOccupancy}/${totalBeds} beds occupied)`);
      
      return {
        success: true,
        message: `Room ${roomNumber} is now ${status}`
      };
    } catch (error: any) {
      console.error('❌ Error updating room availability:', error);
      return {
        success: false,
        message: `Failed to update room: ${error?.message || error}`
      };
    }
  }

  // Sync room statuses with actual patient data
  static async syncRoomStatuses(): Promise<{ success: boolean; message: string; updatedRooms: number }> {
    try {
      console.log('🔄 Starting room status synchronization...');
      
      // Get all rooms
      const roomsRef = collection(db, this.COLLECTION_NAME);
      const roomsSnapshot = await getDocs(roomsRef);
      
      // Get all active patients (not discharged)
      const patientsRef = collection(db, 'patients');
      const patientsQuery = query(patientsRef, where('statusText', '!=', 'Discharged'));
      const patientsSnapshot = await getDocs(patientsQuery);
      
      const patients = patientsSnapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data()
      }));
      
      console.log(`📊 Found ${roomsSnapshot.size} rooms and ${patients.length} active patients`);
      
      let updatedRooms = 0;
      
      // Process each room
      for (const roomDoc of roomsSnapshot.docs) {
        const roomData = roomDoc.data();
        const roomNumber = roomData.roomNumber || roomData.number;
        
        // FIX MISSING BED DATA - Check if room has proper bed configuration
        let totalBeds = roomData.capacity || roomData.totalBeds || 0;
        let needsBedFix = false;
        
        // If no beds configured, set default based on room type
        if (totalBeds === 0) {
          console.warn(`⚠️ Room ${roomNumber} has 0 beds, applying default...`);
          needsBedFix = true;
          // Default beds based on room type
          const roomType = String(roomData.roomType || roomData.type || '').toLowerCase();
          if (roomType.includes('icu')) {
            totalBeds = 6;
          } else if (roomType.includes('vip') || roomType.includes('private')) {
            totalBeds = 1;
          } else {
            totalBeds = 10; // General ward default
          }
        }
        
        // Generate or fix bed labels
        let bedLabels = roomData.bedLabels || [];
        if (!Array.isArray(bedLabels) || bedLabels.length === 0 || bedLabels.length !== totalBeds) {
          console.warn(`⚠️ Room ${roomNumber} has invalid bedLabels, regenerating...`);
          needsBedFix = true;
          bedLabels = Array.from({ length: totalBeds }, (_, i) => `${roomNumber}${String.fromCharCode(65 + i)}`);
        }
        
        // Find patients assigned to this room
        const roomPatients = patients.filter((p: any) => 
          p.room === roomNumber || p.roomNumber === roomNumber || p.room === roomDoc.id
        );
        
        console.log(`🏥 Room ${roomNumber}: ${roomPatients.length} patients assigned, ${totalBeds} total beds`);
        
        // Initialize all beds as available
        let updatedBeds: Array<{
          bedNumber: string;
          isOccupied: boolean;
          patientId?: string;
          patientName?: string;
          occupiedAt?: string;
        }> = bedLabels.map((bedNum: string) => ({
          bedNumber: bedNum,
          isOccupied: false
        }));
        
        // Mark beds occupied by actual patients
        roomPatients.forEach((patient: any) => {
          const patientBed = patient.bedNo || patient.bedNumber;
          if (patientBed) {
            const bedIndex = updatedBeds.findIndex((bed: any) => bed.bedNumber === patientBed);
            if (bedIndex !== -1) {
              updatedBeds[bedIndex] = {
                bedNumber: patientBed,
                isOccupied: true,
                patientId: patient.id,
                patientName: patient.name,
                occupiedAt: patient.admissionDate || new Date().toISOString()
              };
            }
          }
        });
        
        // Calculate current occupancy
        const currentOccupancy = updatedBeds.filter((bed: any) => bed.isOccupied).length;
        
        // Determine room status
        let roomStatus: Room['status'] = 'Available';
        let statusColor = '#10B981';
        
        if (currentOccupancy === totalBeds) {
          roomStatus = 'Occupied';
          statusColor = '#EF4444';
        } else if (currentOccupancy > 0) {
          roomStatus = 'Partially Occupied';
          statusColor = '#F59E0B';
        }
        
        // Check if update is needed
        const needsUpdate = 
          needsBedFix ||
          roomData.status !== roomStatus ||
          roomData.currentOccupancy !== currentOccupancy ||
          JSON.stringify(roomData.availableBeds) !== JSON.stringify(updatedBeds);
        
        if (needsUpdate) {
          console.log(`⚠️ Room ${roomNumber} needs update: ${roomData.status} → ${roomStatus}, beds: ${totalBeds}`);
          
          // Update room in Firebase with complete bed structure
          await updateDoc(doc(db, this.COLLECTION_NAME, roomDoc.id), {
            availableBeds: updatedBeds,
            currentOccupancy: currentOccupancy,
            status: roomStatus,
            statusColor: statusColor,
            isOccupied: currentOccupancy > 0,
            occupancy: {
              current: currentOccupancy,
              total: totalBeds
            },
            bedLabels: bedLabels,
            totalBeds: totalBeds,
            capacity: totalBeds,
            updatedAt: new Date().toISOString()
          });
          
          updatedRooms++;
          console.log(`✅ Room ${roomNumber} synced: ${roomStatus} (${currentOccupancy}/${totalBeds} beds)`);
        } else {
          console.log(`✓ Room ${roomNumber} already in sync`);
        }
      }
      
      console.log(`✅ Room synchronization complete. Updated ${updatedRooms} rooms.`);
      
      return {
        success: true,
        message: `Successfully synchronized ${updatedRooms} room(s)`,
        updatedRooms
      };
    } catch (error: any) {
      console.error('❌ Error syncing room statuses:', error);
      return {
        success: false,
        message: `Failed to sync room statuses: ${error.message}`,
        updatedRooms: 0
      };
    }
  }

  // Calculate room statistics
  static calculateRoomStats(rooms: Room[]): RoomStats {
    const totalRooms = rooms.length;
    const occupiedRooms = rooms.filter(room => room.status === 'Occupied').length;
    const availableRooms = rooms.filter(room => room.status === 'Available').length;
    const maintenanceRooms = rooms.filter(room => room.status === 'Under Maintenance').length;
    const occupancyRate = totalRooms > 0 ? Math.round((occupiedRooms / totalRooms) * 100) : 0;
    
    const totalBeds = rooms.reduce((sum, room) => sum + (room.capacity || room.totalBeds || 1), 0);
    const occupiedBeds = rooms.reduce((sum, room) => {
      if (room.status === 'Occupied') {
        return sum + (room.occupancy?.current || (room.patientName ? 1 : 0));
      }
      return sum;
    }, 0);
    const availableBeds = totalBeds - occupiedBeds;
    const bedOccupancyRate = totalBeds > 0 ? Math.round((occupiedBeds / totalBeds) * 100) : 0;
    
    return {
      totalRooms,
      occupiedRooms,
      availableRooms,
      maintenanceRooms,
      occupancyRate,
      totalBeds,
      occupiedBeds,
      availableBeds,
      bedOccupancyRate
    };
  }

  // Get room by ID
  static async getRoomById(roomId: string): Promise<Room | null> {
    try {
      console.log('🔍 Fetching room by ID...', roomId);
      
      const roomRef = doc(db, this.COLLECTION_NAME, roomId);
      const roomDoc = await getDoc(roomRef);
      
      if (roomDoc.exists()) {
        const firebaseRoom = { id: roomDoc.id, ...roomDoc.data() };
        return this.transformFirebaseRoom(firebaseRoom);
      }
      
      console.log(`❌ Room with ID ${roomId} not found`);
      return null;
    } catch (error) {
      console.error('❌ Error fetching room by ID:', error);
      return null;
    }
  }
}

export default RoomManagementService;